<template>
  <div class="w-full mb-2">
    <!-- Table -->
    <div class="rounded border flex flex-col max-h-full h-full overflow-hidden">
      <div class="overflow-x-auto flex-grow relative">
        <div class="overflow-y-auto" :style="{ height: height }">
          <table class="min-w-max w-full text-center">
            <thead class="bg-gray-200 rounded-t-xl">
              <tr>
                <th v-for="col in columns" :key="col.field" scope="col" :style="columnWidthStyle(col)"
class="px-4 py-2 text-sm lg:text-lg font-semibold uppercase tracking-wider select-none text-gray-500 border-r border-gray-300 last:border-r-0 whitespace-nowrap"                  >
                  <slot :name="`header:${col.field}`" :column="col">
                    <!-- default header if no slot is provided -->
                    <button v-if="col.sortable !== false"
                        class="inline-flex items-center gap-1 uppercase font-semibold"
                        @click="col.filter ? openFilterModal(col) : toggleSort(col.field)">
                        <span>{{ col.label }}</span>
                        <span class="ml-1" v-if="!col.filter && sortBy === col.field">
                          <span v-if="sortDir === 'asc'">▲</span>
                          <span v-else>▼</span>
                        </span>
                    </button>
                    <span v-else>{{ col.label }}</span>
                  </slot>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="displayedRows.length === 0" class="no-records">
                <td :colspan="columns.length" class="px-10 pt-16 text-center text-grey-dark italic">
                  <!-- {{ emptyText }} -->
                  <div class="flex flex-col items-center justify-center">
                    <img src="@/assets/images/papsi_box.png" class="w-24 opacity-75">
                    <p class="text-sm mt-4 text-gray-500">No records found.</p>
                  </div>
                </td>
              </tr>

              <tr
                v-for="(row, index) in displayedRows"
                :key="row[rowKey] || JSON.stringify(row)"
                class="transition border-b"
                @mouseenter="canHoverIndex ? hoveredRowIndex = index : null"
                @mouseleave="canHoverIndex ? hoveredRowIndex = -1 : null"
                :class="[

                  index % 2 === 0 ? 'bg-gray-50' : 'bg-white',

                  canHoverIndex && (hoveredRowIndex === index || selectedRowIndex === index)
                    ? 'bg-gray-50'
                    : '',

                  hover && !isRowDisabled(row) && !hasCustomRowClass(row)
                    ? 'table-row-hover cursor-pointer'
                    : (hover && !isRowDisabled(row) ? 'cursor-pointer' : ''),

                  typeof rowClass === 'function' ? rowClass(row) : '',
                  isRowDisabled(row) ? 'bg-gray-50 text-white cursor-not-allowed opacity-75' : ''
                ]"
                @click="$emit('row-click', row)"
                @dblclick="$emit('row-dblclick', row)"
              >

                <td
                  v-for="col in columns"
                  :key="col.field"
                  :style="columnWidthStyle(col)"
                  class="px-3 lg:px-5 py-1 text-xs lg:text-base text-gray-500 select-none border-r border-gray-200 last:border-r-0 whitespace-nowrap"                  :class="[cellAlign(col), cellColor(col, row), { 'bg-warning-lighter': col.field === highlightColumn }]"
                  @dblclick="editable && startEdit(row, col.field)"
                >

                  <!-- Edit mode: input field -->
                  <input
                    v-if="isEditing(row, col.field)"
                    v-model="editValue"
                    type="text"
                    class="w-full px-2 py-1 border rounded
                          focus:outline-none focus:ring-2 focus:ring-primary"
                    @keydown="handleEditKeydown($event, row, col.field)"
                    @blur="cancelEdit"
                    autofocus
                  />
                  <!-- View mode: slot or formatted value -->
                  <template v-else>
                    <slot :name="`cell:${col.field}`" :row="row" :value="row[col.field]">
                      {{ formatValue(row[col.field]) }}
                    </slot>
                  </template>
                </td>
              </tr>

            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ✅ Pagination (only if enabled) -->
    <div v-if="pagination" class="mt-2 flex flex-col lg:flex-row items-start lg:items-center justify-between">
      <div class="flex flex-row items-center justify-start" style="gap: 0.5rem;">
        <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div class="flex items-center gap-2">
            <label class="lg:text-sm text-xs text-gray-600 mr-0 lg:mr-2">Rows per page: </label>
            <select v-model.number="pageSize" class="rounded border px-1 py-1 focus:outline-none text-sm">
              <option v-for="n in pageSizeOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>
        <div class="lg:text-sm text-xs text-grey-dark italic">
          Showing
          <span class="font-medium">{{ (sortedRows.length > 0 ? startIndex + 1 : 0).toLocaleString() }}</span>–
          <span class="font-medium">{{ endIndex.toLocaleString() }}</span>
          of
          <span class="font-medium">{{ filteredRows.length.toLocaleString() }}</span>
        </div>
      </div>

      <div class="flex items-center text-sm my-1" style="gap: 0.5rem;">
        <button class="flex justify-center items-center rounded-full border p-1 text-sm disabled:opacity-40 bg-white"
          :class="{ 'opacity-50 cursor-default': page === 1, 'hover:bg-white': page != 1 }" :disabled="page === 1"
          @click="page = 1">
          <img src="@/assets/images/papsi_first.svg" class="w-6 h-6" />
        </button>
        <button class="flex justify-center items-center rounded-full border p-1 text-sm disabled:opacity-40 bg-white"
          :class="{ 'opacity-50 cursor-default': page === 1, 'hover:bg-white': page != 1 }" :disabled="page === 1"
          @click="page--">
          <img src="@/assets/images/papsi_prev.svg" class="w-6 h-6" />
        </button>
        <span class="text-xs lg:text-sm">
          Page {{ page.toLocaleString() }} / {{ totalPages.toLocaleString() }}
        </span>
        <button class="flex justify-center items-center rounded-full border p-1 text-sm disabled:opacity-40 bg-white"
          :class="{ 'opacity-50 cursor-default': page === totalPages, 'hover:bg-white': page != totalPages }"
          :disabled="page === totalPages || totalPages === 0" @click="page++">
          <img src="@/assets/images/papsi_right.svg" class="w-6 h-6" />
        </button>
        <button class="flex justify-center items-center rounded-full border p-1 text-sm disabled:opacity-40 bg-white"
          :class="{ 'opacity-50 cursor-default': page === totalPages, 'hover:bg-white': page != totalPages }"
          :disabled="page === totalPages || totalPages === 0" @click="page = totalPages">
          <img src="@/assets/images/papsi_last.svg" class="w-6 h-6" />

        </button>
      </div>
    </div>
  </div>
</template>

<script>
import mixin from '@/mixin';

export default {
  mixins: [mixin],
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, required: true },
    rowKey: { type: String, default: "id" },
    emptyText: { type: String, default: "No records found" },
    pageSizeOptions: { type: Array, default: () => [5, 10, 20, 50, 100] },
    initialSort: { type: Object, default: () => null },
    striped: { type: Boolean, default: true },
    hover: { type: Boolean, default: true },
    pagination: { type: Boolean, default: true },
    rowClass: { type: Function, default: null },
    height: { type: String, default: '61vh' },
    editable: { type: Boolean, default: false },
    disabledRows: { type: Array, default: () => [] },
    // optional column highlighting name
    highlightColumn: { type: String, default: null },
    // ❗ renamed from `can_hoverIndex` to `canHoverIndex` so that Vue's
    //    kebab‑case/attribute normalisation works correctly.  Underscores
    //    break the automatic casing conversion and the prop was always
    //    false when passed as an HTML attribute.
    canHoverIndex: { type: Boolean, default: false }
  },
  data() {
    return {
      hoveredRowIndex: -1,  
      selectedRowIndex: 0,
      // remember how the last interaction occurred so we can ignore
      // accidental double‑clicks caused by keyboard-generated click events
      lastInteraction: null, // 'mouse' | 'keyboard' | null
      pageSize: 20,
      page: 1,
      sortBy: this.initialSort ? this.initialSort.by : null,
      sortDir: this.initialSort ? this.initialSort.dir : "asc",
      filterModal: { open: false, column: null },
      columnFilters: {},
      editingCell: null,  // { rowKey, field }
      editValue: '',      // current edit value
    };
  },
  mounted() {
    if (this.canHoverIndex) {
      window.addEventListener("keydown", this.handleArrowNavigation);
    }
  },
  beforeUnmount() {
    if (this.canHoverIndex) {
      window.removeEventListener("keydown", this.handleArrowNavigation);
    }
  },
  computed: {
    filteredRows() {
      let rows = this.rows || [];
      for (const [key, selected] of Object.entries(this.columnFilters)) {
        if (!selected || selected.length === 0) continue;
        if (selected.includes("__all__")) continue;
        rows = rows.filter((r) => selected.includes(r[key]));
      }
      return rows;
    },

    sortedRows() {
      if (!this.sortBy) return this.filteredRows;

      const arr = [...this.filteredRows];

      arr.sort((a, b) => {
        // ✅ Always keep TOTAL row at the bottom
        if (a.isTotalRow) return 1;
        if (b.isTotalRow) return -1;

        const va = a?.[this.sortBy];
        const vb = b?.[this.sortBy];

        if (va == null && vb == null) return 0;
        if (va == null) return this.sortDir === "asc" ? 1 : -1;
        if (vb == null) return this.sortDir === "asc" ? -1 : 1;

        if (typeof va === "string" || typeof vb === "string") {
          const res = String(va).localeCompare(String(vb));
          return this.sortDir === "asc" ? res : -res;
        }

        const res = va < vb ? -1 : va > vb ? 1 : 0;
        return this.sortDir === "asc" ? res : -res;
      });

      return arr;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedRows.length / this.pageSize));
    },
    startIndex() {
      if (this.sortedRows.length === 0) return 0;
      return Math.min((this.page - 1) * this.pageSize, Math.max(0, this.sortedRows.length - 1));
    },
    endIndex() {
      if (this.sortedRows.length === 0) return 0;
      return Math.min(this.sortedRows.length, this.startIndex + this.pageSize);
    },

    // ✅ computed for display logic
    displayedRows() {
      return this.pagination ? this.sortedRows.slice(this.startIndex, this.endIndex) : this.sortedRows;
    },
  },
  watch: {
    rows() {
      this.page = 1;

      // ✅ reset highlighted row – put the cursor on the first item and also
      //    mimic a hover so it is immediately visible.
      if (this.canHoverIndex) {
        this.selectedRowIndex = 0;
        this.hoveredRowIndex = 0;
      }
    },

    pageSize() {
      this.page = 1;
      this.$emit('page-change', { page: this.page, pageSize: this.pageSize });
    },

    page(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('page-change', { page: newVal, pageSize: this.pageSize });
      }
    },

    // when the selection moves via keyboard, make sure the row is visible
    // and treat it as hovered so styling is consistent with a mouse hover.
    selectedRowIndex(newIdx) {
      if (!this.canHoverIndex || newIdx == null) return;

      this.hoveredRowIndex = newIdx;
      this.$nextTick(() => {
        const tbody = this.$el.querySelector('tbody');
        if (!tbody) return;
        const tr = tbody.children[newIdx];
        if (tr && typeof tr.scrollIntoView === 'function') {
          tr.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      });
    },
  },
  methods: {
    handleArrowNavigation(e) {
      if (!this.displayedRows || this.displayedRows.length === 0) return;

      // Keep track of input type so we can ignore any faux dblclicks that
      // may be generated by the browser when a keyboard "click" follows a
      // real mouse click within the double‑click time window.
      if (e instanceof KeyboardEvent) {
        this.lastInteraction = 'keyboard';
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (this.selectedRowIndex < this.displayedRows.length - 1) {
          this.selectedRowIndex++;
          this.hoveredRowIndex = this.selectedRowIndex;
        }
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (this.selectedRowIndex > 0) {
          this.selectedRowIndex--;
          this.hoveredRowIndex = this.selectedRowIndex;
        }
      }

      if (e.key === "Enter") {
        e.preventDefault();
        // treat Enter as a click on the currently-selected row and prevent
        // the browser from synthesising a second mouse click that would
        // otherwise fire a native dblclick event.
        const row = this.displayedRows[this.selectedRowIndex];
        if (row) {
          this.$emit('row-click', row);
        }
      }
    },

    // wrapper around the native click event so we can note the interaction
    // type and forward the payload.  avoids the parent reacting to a
    // dblclick when the second "click" came from the keyboard.
    handleClick(row, evt) {
      this.lastInteraction = evt instanceof MouseEvent ? 'mouse' : 'keyboard';
      this.$emit('row-click', row);
    },

    handleDblClick(row, evt) {
      // ignore double-clicks that originate from keyboard input; in our
      // scenario the browser synthesises a dblclick after a mouse click and
      // subsequent Enter press, which was interpreted as a second click.
      if (this.lastInteraction === 'keyboard') {
        return;
      }
      this.$emit('row-dblclick', row);
    },

    columnWidthStyle(col) {
      if (!col.width) return {};

      return {
        width: col.width,
        minWidth: col.width,
        maxWidth: col.width,
      };
    },
    cellColor(col, row) {
      // ✅ Skip coloring if the row is disabled
      if (this.isRowDisabled(row)) return '';

      // allow string or function
      if (!col.color) return '';

      if (typeof col.color === 'function') {
        return col.color(row, col);
      }

      return col.color; // string class
    },
    handleEditKeydown(e, row, field) {
      // ✅ Allow F6 only
      if (e.key === "F6") {
        e.preventDefault();
        this.$emit("f6-pressed", { row, field });
        return;
      }

      // ❌ Block ALL typing and editing keys
      e.preventDefault();
    },
    toggleSort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortBy = key;
        this.sortDir = "asc";
      }
    },
    openFilterModal(col) {
      this.filterModal.open = true;
      this.filterModal.column = col;
      if (!this.columnFilters[col.field]) {
        this.columnFilters[col.field] = ["__all__"];
      }
    },
    applyFilters() {
      this.page = 1;
      this.filterModal.open = false;
    },
    uniqueValues(key) {
      const values = (this.rows || []).map((r) => r[key]).filter((v) => v != null);
      return [...new Set(values)];
    },
    toggleAll(key) {
      if (this.columnFilters[key].includes("__all__")) {
        this.columnFilters[key] = ["__all__"];
      }
    },
    uncheckAllIfPartial(key) {
      const values = this.uniqueValues(key);
      if (this.columnFilters[key].includes("__all__") && this.columnFilters[key].length > 1) {
        this.columnFilters[key] = this.columnFilters[key].filter((v) => v !== "__all__");
      }
      if (this.columnFilters[key].length === values.length) {
        this.columnFilters[key] = ["__all__"];
      }
    },
    cellAlign(col) {
      switch (col.align) {
        case "center":
          return "text-center";
        case "right":
          return "text-right";
        default:
          return "text-left";
      }
    },
    formatValue(v) {
      if (v == null) return ""; // show blank instead of "—"
      return v;
    },
    isRowDisabled(row) {
      return this.disabledRows.includes(row[this.rowKey]);
    },
    hasCustomRowClass(row) {
      if (typeof this.rowClass !== "function") return false;
      const cls = this.rowClass(row);
      return !!cls && cls !== "";
    },
    startEdit(row, field) {
      const col = this.columns.find(c => c.field === field);
      // Only allow editing if column has editable: true
      if (!col?.editable) return;
      // Prevent editing disabled rows
      if (this.isRowDisabled(row)) return;

      const rowKeyValue = row[this.rowKey];
      this.editingCell = { rowKey: rowKeyValue, field };
      this.editValue = String(row[field] || '');
    },
    isEditing(row, field) {
      if (!this.editingCell) return false;
      return this.editingCell.rowKey === row[this.rowKey] && this.editingCell.field === field;
    },
    saveEdit(row, field) {
      if (!this.editingCell) return;

      const oldValue = row[field];
      const newValue = this.editValue;

      // Update the row
      row[field] = newValue;

      // Emit event for parent to handle backend call
      this.$emit('cell-edit', {
        rowKey: row[this.rowKey],
        field,
        oldValue,
        newValue,
        row
      });

      this.editingCell = null;
      this.editValue = '';
    },
    cancelEdit() {
      this.editingCell = null;
      this.editValue = '';
    },
  },
};
</script>

<style scoped>


th {
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
}

thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.no-records {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none; 
}

.table-row-hover {
  transition: filter 0.15s ease;
}

.table-row-hover:hover {
  filter: brightness(0.97);
}
</style>
